import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  Routes,
  useLocation,
  useNavigationType,
} from 'react-router';
import * as Sentry from '@sentry/react';

import { SENTRY_STAGE } from '@/lib/constants';

// Capability-bearing tokens that must never leave in an error report: the
// `/q/:token` quote share link, and the `/public/calendar/:token` `.ics` feed
// URL (the SPA never navigates there, but the minted URL string can still land
// in a breadcrumb). Strip the token from any URL-ish string before Sentry sends
// it. Route names are already parameterized; this covers `request.url`, spans,
// and breadcrumbs.
function redactShareToken(value: string): string {
  return value.replace(
    /(\/(?:q|public\/calendar)\/)[^/?#\s]+/g,
    '$1[redacted]',
  );
}

Sentry.init({
  // TODO: replace with your Sentry DSN
  dsn: '',
  enabled:
    SENTRY_STAGE !== 'development' && Boolean(import.meta.env.VITE_SENTRY_DSN),
  environment: SENTRY_STAGE,
  integrations: [
    Sentry.reactRouterV7BrowserTracingIntegration({
      createRoutesFromChildren,
      matchRoutes,
      useEffect,
      useLocation,
      useNavigationType,
    }),
  ],
  sendDefaultPii: true,
  beforeSend(event) {
    if (event.request?.url) {
      event.request.url = redactShareToken(event.request.url);
    }
    if (typeof event.request?.query_string === 'string') {
      event.request.query_string = redactShareToken(event.request.query_string);
    }
    if (event.transaction) {
      event.transaction = redactShareToken(event.transaction);
    }
    return event;
  },
  beforeBreadcrumb(breadcrumb) {
    for (const key of ['url', 'from', 'to'] as const) {
      const value = breadcrumb.data?.[key];
      if (typeof value === 'string') {
        breadcrumb.data![key] = redactShareToken(value);
      }
    }
    if (typeof breadcrumb.message === 'string') {
      breadcrumb.message = redactShareToken(breadcrumb.message);
    }
    return breadcrumb;
  },
});

export const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);
